// Frontend/src/lib/clearCaches.ts
//
// Take the service worker's API response cache off the device.
//
// `/api/` is NetworkOnly as of the payment-refresh fix, so nothing new is
// written and nothing cached is ever served again. That closes the bug --
// the invoice list the service worker used to hand back after a payment,
// taken before the payment -- but shipping the fix does not clean up after
// it. An already-installed PWA activates the new service worker and stops
// writing; whatever the old one wrote stays on disk.
//
// That matters beyond staleness. `api-cache` was keyed by URL alone, with
// nothing in the key identifying the user, and carried no expiration, so a
// device holds every API response it ever fetched: invoices, payments,
// residents, notification bodies. On a shared machine -- a building office
// tablet, two HOA admins on one laptop -- that is one person's data sitting
// in another's session. Anything under these names is by definition from a
// build that predates the fix and is safe to drop.
//
// `api-cache-v2` is listed too. This branch never wrote it, but a device that
// has run a staging build may be carrying it, and dropping a cache that is
// not there costs nothing.
const LEGACY_CACHE_NAMES = ["api-cache", "api-cache-v2"];

/**
 * Drop the API response caches written by earlier builds.
 *
 * Never throws and never blocks startup: the browser may deny cache access
 * entirely (Safari private mode, storage pressure, no service worker
 * support), and failing to clear a cache is worth logging, not worth
 * stopping the app from booting. After the first boot on a new build this
 * finds nothing and costs one `caches.keys()`.
 */
export async function purgeLegacyCaches(): Promise<void> {
  try {
    if (typeof caches === "undefined") return;
    const names = await caches.keys();
    const stale = names.filter((name) => LEGACY_CACHE_NAMES.includes(name));
    if (stale.length === 0) return;
    await Promise.all(stale.map((name) => caches.delete(name)));
    console.info("[pwa] removed cache from a previous build:", stale.join(", "));
  } catch (error) {
    console.error("Failed to purge legacy caches:", error);
  }
}
